import { injectable, inject } from 'inversify';
import { ok, err } from '../../../tracking-api';
import { CollectionDataStore } from '../store';
import { ErrorHandlingScorm2004Client, ErrorHandlingScorm2004ClientSymbol,
         Scorm2004CommandError } from '@/app/tracking/scorm/2004/client';
import { timeIntervalToString, timeIntervalFromSeconds,
         parseTimeInterval, defaultTimeInterval, parseTime, defaultTime, timeToString } from '../api';

export interface Scorm2004InteractionDetails {
  id: string;
  type: string;
  response: string;
  result: 'passed' | 'failed' | 'neutral' | number;
  timestamp: Date;
  durationSeconds: number;
  description: string;
}

export type InteractionErrorCode = 'query-error' | 'update-error';
export interface InteractionError {
  code: InteractionErrorCode;
  details: unknown;
}

@injectable()
export class Scorm2004InteractionDataStore {

  private collection: CollectionDataStore<Scorm2004InteractionDetails, Scorm2004CommandError>;

  constructor(
    @inject(ErrorHandlingScorm2004ClientSymbol)
    private readonly client: ErrorHandlingScorm2004Client) {
    this.collection = new CollectionDataStore(() => this.queryCount(),
                                              (index: number) => this.queryId(index),
                                              (index: number) => this.loadInteraction(index),
                                              (index: number,
                                               details: Scorm2004InteractionDetails
                                              ) => this.saveInteraction(index, details)
                                             );
  }

  public async update(interaction: Scorm2004InteractionDetails) {
    const result = await this.collection.upsert(interaction);
    if (!result.success) {
      return err({
        code: 'update-error' as InteractionErrorCode,
        details: result.error
      });
    }

    return ok(result.value);
  }

  public async insert(interaction: Scorm2004InteractionDetails) {
    const result = await this.collection.insert(interaction);
    if (!result.success) {
      return err({
        code: 'update-error' as InteractionErrorCode,
        details: result.error
      });
    }

    return ok(result.value);
  }

  public async queryAll(id: string) {
    const result = await this.collection.queryAll(id);
    if (!result.success) {
      return err({
        code: 'query-error' as InteractionErrorCode,
        details: result.error
      });
    }

    return ok(result.value);
  }

  public async get(id: string) {
    const result = await this.collection.queryLast(id);
    if (!result.success) {
      return err({
        code: 'query-error' as InteractionErrorCode,
        details: result.error
      });
    }

    return ok(result.value);
  }

  private async queryCount() {
    const countResult = await this.client.getValue('cmi.interactions._count');
    if (!countResult.success) {
      return err(countResult.error);
    }

    const parsed = Number.parseInt(countResult.value, 10);
    if (isNaN(parsed)) {
      throw new Error(`Invalid count ${countResult.value}`);
    }

    return ok(parsed);
  }

  private queryId(index: number) {
    return this.client.getValue(this.interactionPath(index, 'id'));
  }

  private saveInteraction(index: number, details: Scorm2004InteractionDetails) {

    const items = [
      { path: 'id', value: details.id },
      { path: 'type', value: 'other' },
      { path: 'description', value: details.description.substr(0, 250) },
      { path: 'learner_response', value: details.response.substr(0, 4000) },
      { path: 'result', value: mapResult(details.result) },
      { path: 'latency', value: timeIntervalToString(timeIntervalFromSeconds(details.durationSeconds)) },
      { path: 'timestamp', value: timeToString({ milliseconds: details.timestamp.valueOf() })}
    ];

    for (const item of items) {
      this.client.setValue(this.interactionPath(index, item.path),
                                             item.value.toString());
    }

    this.client.commit();

    return ok(true as true);

    function mapResult(input: 'passed' | 'failed' | 'neutral' | number) {
      switch (input) {
        case 'passed':
          return 'correct';
        case 'failed':
          return 'incorrect';
        case 'neutral':
          return 'neutral';
        default:
          return input;
      }
    }
  }

  private async loadInteraction(index: number) {
    // const self = this;

    const mapper: Mapper<Scorm2004InteractionDetails> = {
      id: [ 'id', x => x ],
      type: [ 'type', (x) => x ],
      description: [ 'description', (x) => x ],
      response: [ 'learner_response', (x) => x ],
      result: [ 'result', (x) => mapResult(x) ],
      durationSeconds: [ 'latency', (x) => (parseTimeInterval(x) ||
            defaultTimeInterval()).milliseconds / 1000 ],
      timestamp: [ 'timestamp', (x) => new Date((parseTime(x) || defaultTime()).milliseconds) ]
    }

    const result: Partial<Scorm2004InteractionDetails> = {};
    for (const key in mapper) {
      const typedKey = key as keyof typeof mapper;
      if (!mapper[typedKey]) { continue; }

      const item = mapper[typedKey]
      const getResult = await this.client.getValue(this.interactionPath(index, item[0]));
      if (!getResult.success) {
        return err(getResult.error);
      }

      result[typedKey] = getResult.value as any;
    }
    
    // const queried: WithScormError<Scorm2004InteractionDetails> = {
    //   id: queryAndMap('id', (x) => x),
    //   type: queryAndMap('type', (x) => x),
    //   description: queryAndMap('description', (x) => x),
    //   response: queryAndMap('learner_response', (x) => x),
    //   result: queryAndMap('result', (x) => mapResult(x)),
    //   durationSeconds: queryAndMap('latency', (x) => (parseTimeInterval(x) ||
    //         defaultTimeInterval()).milliseconds / 1000),
    //   timestamp: queryAndMap('timestamp', (x) => new Date((parseTime(x) || defaultTime()).milliseconds))
    // };

    // const result: Partial<Scorm2004InteractionDetails> = {};
    // for (const key in queried) {
    //   const typedKey = key as keyof typeof queried;

    //   if (!queried[typedKey]) { continue; }
    //   const item = queried[typedKey];
    //   if (!item.success) {
    //     return err(item.error);
    //   }

    //   result[typedKey] = item.value as any;
    // }

    return ok(result as Scorm2004InteractionDetails);

    // async function queryAndMap<T>(member: string, mapper: (value: string) => T) {
    //   const getResult = await self.client.getValue(self.interactionPath(index, member));
    //   if (!getResult.success) {
    //     return err(getResult.error);
    //   }

    //   return ok(mapper(getResult.value));
    // }

    function mapResult(input: string): 'passed' | 'failed' | 'neutral' | number {
      switch (input) {
        case 'correct':
          return 'passed';
        case 'incorrect':
          return 'failed';
        case 'neutral':
          return 'neutral';
        case 'unanticipated':
          return 'neutral';
        default:
          const num = parseFloat(input);
          if (isNaN(num)) {
            return 'neutral';
          }

          return num;
      }
    }
  }

  private interactionPath(index: number, member: string) {
    return `cmi.interactions.${index}.${member}`;
  }
}

type Mapper<T> = {
  [P in keyof T]: [ string, (input: string) => T[P] ]
};
